import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core'
import { BehaviorSubject, take, takeUntil, throttleTime } from 'rxjs'

import { ExploreComponent } from "./explore/explore.component"
import { CollectionsComponent } from "./collections/collections.component"
import { SubjectsComponent } from "./subjects/subjects.component"
import { GroupsComponent } from "./groups/groups.component"
import { LessonsComponent } from "./lessons/lessons.component"
import { LessonComponent } from "./lesson/lesson.component"

import { LibraryNavigation, LibrarySelection, LibraryService, PopDataType, UserService } from '@nx-superprep/backend/firebase'
import { ResponsiveComponent } from '@nx-superprep/shared'

import { MatSidenav, MatSidenavContainer, MatSidenavContent, MatSidenavModule } from '@angular/material/sidenav'
import { ActivatedRoute, NavigationEnd, NavigationStart, RouterEvent } from '@angular/router'
import { log, resolveProperty } from '@nx-superprep/utils'
import { DotFlashingComponent } from '@nx-superprep/shared'

import { PopUISidenavFixDirective } from './sidenav-fix.directive';

@Component({
  selector: 'pop-ui-library',
  standalone: true,
  templateUrl: './library.component.html',
  styleUrls: ['./library.component.scss'],
  imports: [
    ExploreComponent,
    CollectionsComponent,
    SubjectsComponent,
    GroupsComponent,
    LessonsComponent,
    LessonComponent,
    MatSidenavModule,
    DotFlashingComponent,
    PopUISidenavFixDirective,
  ],
})
export class LibraryComponent extends ResponsiveComponent implements OnInit, OnDestroy {
  @ViewChild('librarysidenavContainer') private container?: MatSidenavContainer
  @ViewChild('librarycontent') private content?: MatSidenavContent
  @ViewChild('librarysidenav') private sidenav?: MatSidenav

  @Input() subjectId!: string | undefined
  @Input() groupId!: string | undefined
  @Input() levelId!: string | undefined
  @Input() moduleId!: string | undefined

  private _contentMarginRight = '0px'
  get contentMarginRight() {
    return this._contentMarginRight
  }
  set contentMarginRight(value) {
    if (this._contentMarginRight === value) { return }
    this._contentMarginRight = value
  }

  override onRouterEvent(event: RouterEvent): void {
    super.onRouterEvent(event)
    if (event instanceof NavigationStart) {
      const detail = this.state.selection?.detail
      if (detail && 'subscribed' in detail) {
        const subscribed = resolveProperty(detail.subscribed)
        if (subscribed) { this.libraryService.navigationState = undefined }
      } else {
        this.libraryService.navigationState = undefined
      }
    }
    if (event instanceof NavigationEnd) {
      this.state = {navigation: { current: { route: 'library', type: 'root' }}, selection:{didChange:true}}
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy()
    this.libraryService.navigationState = undefined // reset navigation state
  }

  readonly route = inject(ActivatedRoute)
  readonly userService = inject(UserService)
  isLoaded = false

  override ngOnInit(): void {
    super.ngOnInit()
    this.isLoaded = this.libraryService.librarySections.length > 0;
    // Check if a user is logged in, if not, create an anonymous user
    (this.userService.isUserActive ? Promise.resolve(this.userService.activeUser) : this.userService.signInWithClassroomId()).then((user) => {
      log.debug(user)
      this.initializeLibrary()
    })
    this.resizeSubject.pipe(
      throttleTime(200, undefined, { leading: true, trailing: true }),
      takeUntil(this.onDestroy)
    ).subscribe(() =>   this.updateContentWidth())
  }

  private initializeLibrary(): void {
    // Show cached data immediately if available
    this.isLoaded = this.libraryService.cachedLibrarySections.length > 0
    // Bind parameters to inputs
    this.subjectId = this.route.snapshot.paramMap.get('subjectId') ?? undefined
    this.levelId = this.route.snapshot.paramMap.get('levelId') ?? undefined
    this.moduleId = this.route.snapshot.paramMap.get('moduleId') ?? undefined
    const showModule = this.moduleId && !(this.subjectId||this.levelId)
    // direct access to lesson using its moduleId
    const selection = {didChange:true, ...showModule ? {detail:{id:this.moduleId}} : {}} as LibrarySelection
    this.state = this.libraryService.navigationState ?? {navigation: { current: { route: 'library', type: 'root' }}, selection}
    // Subscribe to the librarySections observable to get updates
    this.libraryService.getStudyLibrarySections().pipe(
      takeUntil(this.onDestroy)
    ).subscribe((sections) => {
      log.debug('Library sections updated:', sections)
      this.isLoaded = sections.length > 0
      if (!this.isLoaded) { return }
      // use parameters to navigate to the corresponding section
      if (this.subjectId) {
        const subjects = sections.find(s => s.title === 'Subjects')
        let item = subjects?.items.find(i => i.itemId === this.subjectId)
        const state: LibraryNavigation = {
          navigation: { current: { route: 'subjects', type: 'back' }},
          parent: {navigation: { current:{ route: 'library', type: 'root' }}, selection:{didChange:false}},
          selection:{didChange:true,item}
        }
        if (this.levelId) {
          this.libraryService.getGroupForSubject(this.subjectId).pipe(
            takeUntil(this.onDestroy),
            take(1)
          ).subscribe((groups) => {
            if (!groups.length) { return }
            const g = groups.find(g => g.levelIds.includes(this.levelId??''))
            if (!g) { return }
            this.libraryService.getLevelForGroup(g.id).pipe(
              takeUntil(this.onDestroy),
              take(1)
            ).subscribe((levels) => {
              const l = levels.find(l => l.id === this.levelId)
              if (!l) { return }
              item = {...l, title:l.title, itemId:l.id, dataType: PopDataType.LevelTemplate}
              if (!item) { return }
              const parent: LibraryNavigation = {
                ...state,
                navigation: {...state.navigation},
              }
              state.navigation.current = { route: 'lessons', type: 'back' }
              state.parent = parent
              state.selection = {didChange:true,item, ...this.moduleId ? {detail:{id:this.moduleId}} : {}}
              this.state = state
            })
          })
        } else {
          this.state = state
        }
      }
    })
  }

  readonly libraryService = inject(LibraryService)

  contentWidth = 1280;

  updateContentWidth() {
    if (!this.content || this.isHandset || this.isTabletPortrait) {
      if (this.contentWidth !== 1280) {
        this.contentWidth = 1280;
      }
      return;
    }
    requestAnimationFrame(() => {
      const sidenavWidth = 650;
      const minGap = 24; // Minimum space between content and sidenav
      const maxContentWidth = 1280;
      const windowWidth = window.innerWidth;
      const isSidenavOpen = this.isSlideOpen[6];

      let newWidth = maxContentWidth;

      if (isSidenavOpen) {
        // Calculate space right of container (viewport edge - container edge)
        const containerRightEdge = (windowWidth - maxContentWidth) / 2 + maxContentWidth;
        const spaceRightOfContainer = windowWidth - containerRightEdge;

        if (spaceRightOfContainer >= sidenavWidth + minGap) {
          // Case 1: Enough space - keep full width
          newWidth = maxContentWidth;
        } else {
          // Case 2: Reduce content width to maintain gap
          const neededReduction = (sidenavWidth + minGap) - spaceRightOfContainer;
          newWidth = Math.max(650, maxContentWidth - neededReduction);
        }
      } else {
        // Sidenav closed - normal behavior
        newWidth = Math.min(windowWidth, maxContentWidth);
      }

      if (this.contentWidth !== newWidth) {
        this.contentWidth = newWidth;
      }

      log.debug(
        `Window: ${windowWidth}px | ` +
        `Content Width: ${this.contentWidth}px | ` +
        `Space Right: ${windowWidth - ((windowWidth - this.contentWidth) / 2 + this.contentWidth)}px | ` +
        `Sidenav: ${this.isSlideOpen[6] ? 'Open' : 'Closed'}`
      );
    });
  }

  onSidenavOpenChange(isOpen: boolean) {
    requestAnimationFrame(() => {
      if (isOpen === this.isSlideOpen[6]) {
        this.updateContentWidth();
      }
    });
  }

  isAnimated = false
  isSlideOpen: boolean[] = Array(7).fill(false)
  isVisible = this.isSlideOpen.map(s => new BehaviorSubject<boolean>(!s))
  isOpen = this.isSlideOpen.map(s => new BehaviorSubject<boolean>(s))

  private _state: LibraryNavigation = {navigation: { current: { route: 'library', type: 'root' }}}

  /**
   * Input for the component state.
   * Updates the state and manages navigation state and content resizing.
   *
   * @param {LibraryNavigation} value - The new state for the component.
   */
  @Input()
  get state() { return this._state; }
  set state(value) {
    const selected = value.navigation.current.route.toLowerCase()
    const detail =  value.selection?.detail
    ? 'lesson'
    : value.selection?.item
      ? ('itemId' in value.selection.item) || ('groupIds' in value.selection.item)
        ? 'subject'
        : 'none'
      : 'none'
    // Store previous state of slide 6 before updating
    const wasSlide6Open = this.isSlideOpen[6];
    // list of lessons with one lesson selected
    this.isSlideOpen[6] = /*selected === 'lessons' &&*/ detail === 'lesson'
    this.isSlideOpen[5] = selected === 'lessons'
    this.isSlideOpen[4] = selected === 'groups' && detail === 'none'
    this.isSlideOpen[3] = selected === 'subjects' && detail === 'subject'
    this.isSlideOpen[2] = selected === 'subjects' && detail === 'none'
    this.isSlideOpen[1] = selected === 'collections'
    this.isSlideOpen[0] = selected === 'library'

    this.collapsed = this.isSlideOpen[6]

    this._state = value
    // Check if slide 6 (sidenav) state changed
    const slide6StateChanged = wasSlide6Open !== this.isSlideOpen[6];

    if (this.state.selection?.didChange) {
      this.libraryService.navigationState = value;
      this.state.selection.didChange = false;
      this.stateChange.emit(this.state);
    }

    if (slide6StateChanged) { this.updateContentWidth(); }

    if (this.search) {
      this.searchChange.emit({...this.search, visible: true, selected: undefined})
    }
  }
  @Output() stateChange = new EventEmitter<LibraryNavigation>()

  get activeSlide() {
    return this.isSlideOpen.findIndex(s => s)
  }

  isSlideVisible(s: number) {
    return this.isSlideOpen[s]
  }
}
